import React, { useRef, useEffect } from "react";
import Helmet from "react-helmet";
import Layout from "../components/layout";
import ComicSlider from "../components/comicSlider";
import ReactGA from "react-ga";
import "url-search-params-polyfill";
import ViewerStateContext from "../context/viewer-state";
import {
  PublishedContext,
  usePublishedContext,
} from "../context/published-state";
import { pvListener, multipleGaTrackingUtil } from "../utils/pvListener";
import faviconGb from "../../static/favicon.png";
import faviconFs from "../../static/favicon_fs.ico";

const getCurrentPage = (maxLength: number) => {
  if (typeof window !== `undefined`) {
    let initialSlide = 1;
    const urlParams = new URLSearchParams(window.location.search);
    // ・page= 以外のパラメータはここでは度外視。
    // ・数値以外の文字が入っていた時点で、デフォルトページ=1 強制。
    // ・先頭ゼロは許容。
    if (
      urlParams.has("page") &&
      !String(urlParams.get("page")).match(/[^0-9]+/g)
    ) {
      initialSlide = parseInt(String(urlParams.get("page")), 10);
    }
    if (initialSlide < 0 || initialSlide > maxLength) {
      initialSlide = 1;
    }
    return initialSlide;
  }
  return 1;
};

interface Props {
  pageContext: Comic;
}

const IndexPage = (props: Props) => {
  const { pageContext } = props;
  const { parentArticleUrl, media } = pageContext;

  const sliderRef = useRef(null);
  const pictureLength = pageContext.pictureUrls.length + 1;
  const title =
    pageContext.title +
    " | " +
    pageContext.captionCommon +
    " " +
    pageContext.labelCommon;

  useEffect(() => {
    if (typeof window !== "undefined" && !(window as any).GA_INITIALIZED) {
      const [trackingIdArray, trackingNameArray] = multipleGaTrackingUtil(
        pageContext.media.gaIds
      );
      ReactGA.initialize(trackingIdArray);
      pvListener(
        pageContext.slug,
        pageContext.num,
        getCurrentPage(pictureLength),
        pageContext.media.gaIds
      );
      (window as any).GA_INITIALIZED = true;
    }
  }, []);

  const mediaId = media.label ? media.label : "gb";
  const favicon = mediaId == "fs" ? faviconFs : faviconGb;
  const mediaAlt = media.alt ? media.alt : "現代ビジネス";
  const mediaUrl = media.homeUrl ? media.homeUrl : "https://gendai.media";

  return (
    <>
      <Helmet
        title={title}
        meta={[
          {
            name: "description",
            content: pageContext.ogDescription,
          },
          {
            property: "og:title",
            content: pageContext.ogTitle || title,
          },
          { property: "og:type", content: "article" },
          { property: "og:site_name", content: pageContext.labelCommon },
          {
            property: "og:url",
            content: `${pageContext.rootUrl}/${pageContext.slug}/${pageContext.num}`,
          },
          {
            property: "og:image",
            content:
              pageContext.ogImageLarge ||
              pageContext.ogImageIcon ||
              `${pageContext.rootUrl}/og_image/large/default_ogp.png`,
          },
          {
            property: "og:description",
            content: pageContext.ogDescription,
          },
          {
            name: "twitter:card",
            content: !pageContext.ogImageIcon
              ? "summary_large_image"
              : "summary",
          },
          {
            name: "twitter:image",
            content:
              pageContext.ogImageIcon ||
              pageContext.ogImageLarge ||
              `${pageContext.rootUrl}/og_image/large/default_ogp.png`,
          },
          { name: "twitter:site", content: "@gendai_biz" },
        ]}
        link={[
          {
            rel: "canonical",
            href: `${pageContext.parentArticleUrl}`,
          },
          {
            rel: "icon",
            type: "image/png",
            href: `${favicon}`,
          },
          {
            rel: "index",
            title: `${mediaAlt}`,
            href: `${mediaUrl}`,
          },
        ]}
      >
        <script>
          {`
              window.dataLayer = window.dataLayer || [];
              window.dataLayer.push({ media: "${mediaId}" });

            `}
        </script>
      </Helmet>

      <PublishedContext.Provider value={usePublishedContext()}>
        <ViewerStateContext.Provider
          value={{ currentPage: getCurrentPage(pictureLength) }}
        >
          <Layout
            sliderRef={sliderRef}
            length={pictureLength}
            parentArticleUrl={parentArticleUrl}
            media={media}
          >
            <ComicSlider sliderRef={sliderRef} comic={pageContext} />
          </Layout>
        </ViewerStateContext.Provider>
      </PublishedContext.Provider>
    </>
  );
};

export default IndexPage;
